'use client';

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import type { ExpenseAccountOption } from '@/lib/finance/expenseAccountMapping';
import { uiViewportHeight } from '@/lib/uiScale';

interface SearchableAccountSelectProps {
  accounts: ExpenseAccountOption[];
  value: string;
  onChange: (accountCode: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isDark?: boolean;
  /** Unique key for persisting recent accounts in localStorage (e.g. clientId) */
  recentKey?: string;
  className?: string;
}

const ACCOUNT_TYPE_ORDER = ['Asset', 'Expense', 'Revenue', 'Liability', 'Equity'] as const;
const ACCOUNT_TYPE_COLORS: Record<string, string> = {
  Asset: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Expense: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Revenue: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Liability: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  Equity: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

const RECENT_STORAGE_KEY = 'ta-finance-recent-gl-accounts';
const MAX_RECENT = 10;

function getRecentAccounts(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(`${RECENT_STORAGE_KEY}:${key}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function pushRecentAccount(key: string, accountCode: string) {
  if (typeof window === 'undefined') return;
  try {
    const current = getRecentAccounts(key);
    const filtered = current.filter((c) => c !== accountCode);
    filtered.unshift(accountCode);
    const trimmed = filtered.slice(0, MAX_RECENT);
    localStorage.setItem(`${RECENT_STORAGE_KEY}:${key}`, JSON.stringify(trimmed));
  } catch {
    // ignore quota errors
  }
}

export function SearchableAccountSelect({
  accounts,
  value,
  onChange,
  placeholder = 'Search GL account…',
  disabled = false,
  isDark = false,
  recentKey,
  className = '',
}: SearchableAccountSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recentCodes = useMemo(
    () => (recentKey && open ? getRecentAccounts(recentKey) : []),
    [recentKey, open],
  );

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.account_code === value) || null,
    [accounts, value],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.account_code.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q),
    );
  }, [accounts, query]);

  const grouped = useMemo(() => {
    const groups: Record<string, ExpenseAccountOption[]> = {};
    for (const acc of filtered) {
      const type = acc.account_type || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(acc);
    }
    return groups;
  }, [filtered]);

  const flatList = useMemo(() => {
    return filtered;
  }, [filtered]);

  const recentAccounts = useMemo(() => {
    if (!recentKey || !recentCodes || recentCodes.length === 0) return [];
    return recentCodes
      .map((code) => accounts.find((a) => a.account_code === code))
      .filter(Boolean) as ExpenseAccountOption[];
  }, [recentCodes, accounts, recentKey]);

  const handleSelect = useCallback(
    (accountCode: string) => {
      onChange(accountCode);
      if (recentKey) pushRecentAccount(recentKey, accountCode);
      setOpen(false);
      setQuery('');
      setHighlightedIndex(0);
    },
    [onChange, recentKey],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const acc = flatList[highlightedIndex];
      if (acc) handleSelect(acc.account_code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = uiViewportHeight() - rect.bottom;
      const dropdownHeight = 300; // approx max-h-72 = 288px + search input
      setDropUp(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    }
    setOpen(!open);
    if (!open) setTimeout(() => inputRef.current?.focus(), 0);
  };

  const baseInputClass = `w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 ${
    isDark
      ? 'border-gray-600 bg-gray-900 text-gray-100'
      : 'border-gray-300 bg-white text-gray-900'
  } ${className}`;

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={`${baseInputClass} flex items-center justify-between text-left ${
          !value ? (isDark ? 'text-gray-500' : 'text-gray-400') : ''
        }`}
      >
        <span className="flex items-center gap-1.5 truncate">
          {selectedAccount ? (
            <>
              <span className="font-medium">{selectedAccount.account_code}</span>
              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>—</span>
              <span className="truncate">{selectedAccount.name}</span>
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute z-[100] w-full rounded-lg border shadow-lg max-h-72 overflow-hidden flex flex-col ${
            dropUp ? 'bottom-full mb-1' : 'mt-1'
          } ${
            isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-white'
          }`}
        >
          {/* Search input */}
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="relative">
              <Search className={`absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlightedIndex(0);
                }}
                placeholder="Search by code or name…"
                className={`w-full rounded-md border pl-7 pr-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isDark
                    ? 'border-gray-600 bg-gray-800 text-gray-100'
                    : 'border-gray-200 bg-white text-gray-900'
                }`}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {flatList.length === 0 ? (
              <div className={`p-3 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No accounts found
              </div>
            ) : (
              <>
                {/* Recent accounts */}
                {recentAccounts.length > 0 && !query && (
                  <div className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Recently Used
                  </div>
                )}
                {recentAccounts.length > 0 && !query && (
                  recentAccounts.map((acc) => {
                    const isSelected = acc.account_code === value;
                    const flatIdx = flatList.findIndex((f) => f.account_code === acc.account_code);
                    return (
                      <button
                        key={`recent-${acc.account_code}`}
                        type="button"
                        onClick={() => handleSelect(acc.account_code)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left transition-colors ${
                          flatIdx === highlightedIndex
                            ? isDark ? 'bg-gray-800' : 'bg-blue-50'
                            : isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <span className="font-medium">{acc.account_code}</span>
                          <span className={`truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{acc.name}</span>
                        </span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {acc.account_type && (
                            <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${ACCOUNT_TYPE_COLORS[acc.account_type] || 'bg-gray-100 text-gray-600'}`}>
                              {acc.account_type}
                            </span>
                          )}
                          {isSelected && <Check className="h-3.5 w-3.5 text-blue-500" />}
                        </span>
                      </button>
                    );
                  })
                )}

                {/* Grouped results */}
                {recentAccounts.length > 0 && !query && (
                  <div className={`my-1 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`} />
                )}
                {ACCOUNT_TYPE_ORDER.map((type) => {
                  const group = grouped[type];
                  if (!group || group.length === 0) return null;
                  return (
                    <div key={type}>
                      <div className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {type}
                      </div>
                      {group.map((acc) => {
                        const isSelected = acc.account_code === value;
                        const flatIdx = flatList.findIndex((f) => f.account_code === acc.account_code);
                        return (
                          <button
                            key={acc.account_code}
                            type="button"
                            onClick={() => handleSelect(acc.account_code)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left transition-colors ${
                              flatIdx === highlightedIndex
                                ? isDark ? 'bg-gray-800' : 'bg-blue-50'
                                : isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2 truncate">
                              <span className="font-medium">{acc.account_code}</span>
                              <span className={`truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{acc.name}</span>
                            </span>
                            <span className="flex items-center gap-1.5 shrink-0">
                              <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${ACCOUNT_TYPE_COLORS[type] || 'bg-gray-100 text-gray-600'}`}>
                                {type}
                              </span>
                              {isSelected && <Check className="h-3.5 w-3.5 text-blue-500" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
                {/* Other types not in the standard order */}
                {Object.entries(grouped)
                  .filter(([type]) => !ACCOUNT_TYPE_ORDER.includes(type as never))
                  .map(([type, group]) => (
                    <div key={type}>
                      <div className={`px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {type}
                      </div>
                      {group.map((acc) => {
                        const isSelected = acc.account_code === value;
                        const flatIdx = flatList.findIndex((f) => f.account_code === acc.account_code);
                        return (
                          <button
                            key={acc.account_code}
                            type="button"
                            onClick={() => handleSelect(acc.account_code)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left transition-colors ${
                              flatIdx === highlightedIndex
                                ? isDark ? 'bg-gray-800' : 'bg-blue-50'
                                : isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex items-center gap-2 truncate">
                              <span className="font-medium">{acc.account_code}</span>
                              <span className={`truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{acc.name}</span>
                            </span>
                            <span className="flex items-center gap-1.5 shrink-0">
                              <span className={`rounded px-1 py-0.5 text-[9px] font-medium ${ACCOUNT_TYPE_COLORS[type] || 'bg-gray-100 text-gray-600'}`}>
                                {type}
                              </span>
                              {isSelected && <Check className="h-3.5 w-3.5 text-blue-500" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
